import * as shallowEqual from 'shallowequal';
import * as _ from 'underscore';

import { FeatureFlags } from '../../../../ts/FeatureFlags';
import { ServerFilterClauses } from '../../../../ts/helpers/ServerFilterClauses';
import { ExplorableItemDefinitions } from '../../../../ts/models/ContentDefinitions';
import { ContentFilterKeyName } from '../../../../ts/models/ContentFilters';
import { ExplorableItemType } from '../../../../ts/models/ContentType';
import { MixedContentType } from '../../../../ts/models/ContentTypes';
import { IFieldFilterClause, IFilterClause, IStringValuedFilterClause } from '../../../../ts/ServerApi/types/IFilterClause';
import { FilterMap } from '../../../state/ContentExplorerState';
import { isFavoritesSpace, isProjectSpace, SpaceInfo, SpaceType } from '../../../state/SpaceInfo';
import { ContentFilterAppliedAction, FilterContentAction } from './FilterActions';

export type InterfaceForContentFilterActions = ContentFilterAppliedAction | FilterContentAction;

export function doesActionCreateDifferentFilter(currentFilter: IStringValuedFilterClause, action: InterfaceForContentFilterActions): boolean {
  const isBrandNewFilterValue = (_.isUndefined(currentFilter) && action.value !== undefined);  // must be undefined because 'null' is a value for showEmbedded filter
  const isOldValueDifferent = (!_.isUndefined(currentFilter) && actionValueDiffersFromFilter(currentFilter, action));
  return isBrandNewFilterValue || isOldValueDifferent;
}

export function doesActionCreateSameFilter(currentFilter: IStringValuedFilterClause, action: InterfaceForContentFilterActions): boolean {
  return !doesActionCreateDifferentFilter(currentFilter, action);
}

function actionValueDiffersFromFilter(currentFilter: IStringValuedFilterClause, action: InterfaceForContentFilterActions): boolean {
  const filterCreatedFromAction = ServerFilterClauses.serverFilterClauses[action.filterType](action.value);
  return !shallowEqual(filterCreatedFromAction, currentFilter);
}

export function cloneFilterMap(original: FilterMap): FilterMap {
  return _.mapObject(original, (filter: IFilterClause) => (filter === null? null : { ...filter}));
}

export function getFilterClausesForTypes(mixedContentFiltersMap: FilterMap,
                                         requestedContentTypes: ReadonlyArray<ExplorableItemType>,
                                         appStateContentTypes: ReadonlyArray<ExplorableItemType>,
                                         projectId: string,
                                         spaceInfo: SpaceInfo
): IFilterClause[] {

  if (!mixedContentFiltersMap) {
    return [];
  }

  let filterKeysToKeep: ContentFilterKeyName[] = [];
  let defaultFilters = {};

  if (hasMultipleContentTypes(requestedContentTypes) /*Requesting mixed content*/) {
    filterKeysToKeep = MixedContentType.filterKeys;
    // no default filters for mixed content

    if (isFavoritesSpace(spaceInfo) || isProjectSpace(spaceInfo)) {
      // recents, with metricsSpaceEnabled, is the only mixed content space that allows filtering by tags
      filterKeysToKeep = _.reject(filterKeysToKeep, (filter) => filter === 'tag');
    }
  } else if (hasMultipleContentTypes(appStateContentTypes)) {
    // TFSID:844413. This handles the case when we're querying for a specific content count
    // but are displaying mixed content.  So we want to use the default filters
    // for the specific content type and the mixed content filter keys.
    // So that the content count matches the number of items displayed
    defaultFilters = ExplorableItemDefinitions[requestedContentTypes[0]].defaultFilters(projectId);
    filterKeysToKeep = MixedContentType.filterKeys;

    if (isFavoritesSpace(spaceInfo) || isProjectSpace(spaceInfo)) {
      // recents, with metricsSpaceEnabled, is the only mixed content space that allows filtering by tags
      filterKeysToKeep = _.reject(filterKeysToKeep, (filter) => filter === 'tag');
    }
  } else {
    filterKeysToKeep = ExplorableItemDefinitions[requestedContentTypes[0]].contentFilterKeys;
    defaultFilters = ExplorableItemDefinitions[requestedContentTypes[0]].defaultFilters(projectId);
  }

  const typeOnlyFilters  = _.pick(mixedContentFiltersMap, filterKeysToKeep);
  const typeOnlyFiltersWithDefaults = _.defaults(typeOnlyFilters, defaultFilters);
  let typeOnlyFiltersWithDefaultAsArray: IFilterClause[] = _.compact(_.values(typeOnlyFiltersWithDefaults));

  if (hasMultipleContentTypes(requestedContentTypes)) {
    typeOnlyFiltersWithDefaultAsArray.push(ServerFilterClauses.ContentType(requestedContentTypes));
  }

  return typeOnlyFiltersWithDefaultAsArray;
}

function hasMultipleContentTypes(shownContentTypes: ReadonlyArray<ExplorableItemType>) {
  return shownContentTypes.length !== 1;
}

export function removeFilterClauseFromFilters(filters: IFilterClause[], filterField: string) {
  return _.filter(filters,
    clause => !!clause && (hasNoField(clause) || (clause as IFieldFilterClause).field !== filterField));
}

function hasNoField(clause: any): boolean {
  return !(clause as IFieldFilterClause).field;
}

type SpaceSupportsStylishFiltering = {
  readonly [P in SpaceType]: boolean;
};

const spaceSupportsStylishFiltering: SpaceSupportsStylishFiltering = {
  'sites': true,
  'favorites': true,
  'recents': true,
  'project': true,
  'site': true,
  'siteuser': true,
  'search': true,
  'unifiedSearch': true,
  'backgroundJobs': true,
  'metric': true,
  'sitetasks': true,
  'siteusers': true,
  'serverusers': true,

  'flow': false,
  'workbook': false,
  'dataRole': false,
  'datasource': false,
  'group': false,
  'home': false,
  'recommendations': false,
  'view': false,
  'sitestatus': false,
  'schedules': false,
  'schedule': false,
  'siteanalysis': false,
  'serveruser': false,
  'serverstatus': false,
  'serveranalysis': false,
  'sitesettings': false,
  'serversettings': false,
  'servertasks': false,
  'catalog': false,
  'groups': false,
  'revisionhistory': false
};

// spaces that are already styled (e.g., not groups) and support filtering
export function doesSpaceSupportStylishFiltering(spaceInfo: SpaceInfo): boolean {
  return spaceSupportsStylishFiltering[spaceInfo.spaceType];
}
